import React, { useEffect, useState } from 'react';
import { Modal, Button, Avatar, Typography, Divider, Descriptions, Alert, Space, Spin } from 'antd';
import { get_user_subscriptionApi } from '../../api/admin.billing.apis';
import {
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  EditOutlined,
} from "@ant-design/icons";
import UserRoleTag from './UserRoleTag';
import UserStatusTag from './UserStatusTag';

const { Title, Text } = Typography;

const UserModalView = ({ visible, user, onClose, onEdit, subscriptionRefreshKey = 0 }) => {
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!visible || !user?._id) {
        setSubscription(null);
        return;
      }
      try {
        setSubscriptionLoading(true);
        const response = await get_user_subscriptionApi(user._id);
        if (response.data.status === 'success') {
          setSubscription(response.data.data?.subscription || null);
        } else {
          setSubscription(null);
        }
      } catch (error) {
        setSubscription(null);
      } finally {
        setSubscriptionLoading(false);
      }
    };
    fetchSubscription();
  }, [visible, user?._id, subscriptionRefreshKey]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) return null;

  return (
    <Modal
      title="User Details"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="edit"
          type="primary"
          icon={<EditOutlined />}
          onClick={onEdit}
        >
          Edit User
        </Button>,
      ]}
      width={800}
    >
      <Space direction="horizontal" align="start" size="middle">
        <Avatar
          size={64}
          style={{ backgroundColor: user.is_verified ? '#52c41a' : '#faad14' }}
        >
          {user.full_name?.charAt(0) || 'U'}
        </Avatar>
        <Space direction="vertical" size={4}>
          <Title level={3}>{user.full_name}</Title>
          <Text type="secondary">{user.email}</Text>
          <Space>
            <UserRoleTag role={user.role} />
            <UserStatusTag user={user} />
          </Space>
        </Space>
      </Space>

      <Divider />

      <Descriptions column={2} bordered>
        <Descriptions.Item label="User ID" span={2}>
          <Text copyable>{user._id}</Text>
        </Descriptions.Item>

        <Descriptions.Item label="Email">
          <Space>
            <MailOutlined />
            {user.email}
          </Space>
        </Descriptions.Item>

        <Descriptions.Item label="Phone">
          <Space>
            <PhoneOutlined />
            {user.phone || 'Not provided'}
          </Space>
        </Descriptions.Item>

        <Descriptions.Item label="Address">
          <Space>
            <EnvironmentOutlined />
            {user.address || 'Not provided'}
          </Space>
        </Descriptions.Item>

        <Descriptions.Item label="Website">
          <Space>
            <GlobalOutlined />
            {user.website_url ? (
              <a href={user.website_url} target="_blank" rel="noopener noreferrer">
                {user.website_url}
              </a>
            ) : 'Not provided'}
          </Space>
        </Descriptions.Item>

        <Descriptions.Item label="Role">
          {user.role.toUpperCase()}
        </Descriptions.Item>

        <Descriptions.Item label="Status">
          {user.is_active ? 'Active' : 'Inactive'}
        </Descriptions.Item>

        <Descriptions.Item label="Verified">
          {user.is_verified ? 'Yes' : 'No'}
        </Descriptions.Item>

        <Descriptions.Item label="Created At" span={2}>
          {formatDate(user.created_at)}
        </Descriptions.Item>

        <Descriptions.Item label="Last Updated" span={2}>
          {formatDate(user.updated_at)}
        </Descriptions.Item>

        <Descriptions.Item label="Last Login" span={2}>
          {formatDate(user.last_login_at)}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Title level={5}>Subscription</Title>
      <Spin spinning={subscriptionLoading}>
        {subscription ? (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Plan">{subscription.plan_name}</Descriptions.Item>
            <Descriptions.Item label="Status">{subscription.status}</Descriptions.Item>
            <Descriptions.Item label="Usage">
              {subscription.usage
                ? `${subscription.usage.used} / ${subscription.usage.limit} requests`
                : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Period End">
              {formatDate(subscription.current_period_end)}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Alert message="No active subscription" type="warning" showIcon />
        )}
      </Spin>

      {user.selected_property && (
        <>
          <Divider />
          <Alert
            message="Connected Property"
            description={`This user has a connected property: ${user.selected_property}`}
            type="info"
            showIcon
          />
        </>
      )}
    </Modal>
  );
};

export default UserModalView;
